import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, X } from "lucide-react";
import { useSession } from "@/lib/member-auth";
import { MEMBERS_PORTAL_LIVE } from "@shared/members-launch";
import { trialWindowOpen, trialEndLabel } from "@shared/pro-pricing";
import { MEMBER_WORKSHOP_PERCENT_OFF } from "@shared/member-discount";
import { drawMenuButtons } from "@/lib/ds-drawn-buttons";
import "@/styles/menu-vivid.css";

/* ─── Types ──────────────────────────────────────────────────────────────── */

interface NavPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavLink {
  label: string;
  href: string;
  desc?: string;
  external?: boolean;
}

interface NavSection {
  title: string;
  links?: NavLink[];
  href?: string;
  desc?: string;
  testId?: string;
}

/* ─── Navigation Data ────────────────────────────────────────────────────── */

const PLUS_NAV_DESC = trialWindowOpen()
  ? `Ongoing AI training: live sessions, tools, and ${MEMBER_WORKSHOP_PERCENT_OFF}% off workshops. Free through ${trialEndLabel()}`
  : `Ongoing AI training: live sessions, tools, and ${MEMBER_WORKSHOP_PERCENT_OFF}% off workshops`;

const NAV_ITEMS: NavSection[] = [
  {
    title: "Services",
    links: [
      { label: "Performance Media", href: "/services", desc: "Full-funnel paid media strategy across every channel" },
      { label: "Creative", href: "/creative-services", desc: "Ad creative, video, and visual storytelling" },
      { label: "Strategy & Market Research", href: "/landing-page-ab-testing", desc: "Systematic testing and research to maximize performance" },
      { label: "SEO & AI Search", href: "/seo-agency-services", desc: "Organic visibility and AI answer engine optimization" },
      { label: "AI Transformation", href: "/ai-transformation-services", desc: "Modernize your marketing with AI infrastructure" },
      { label: "Attribution & Measurement", href: "/measurement", desc: "Full-funnel tracking, modeling, and reporting" },
    ],
  },
  { title: "Our Client Results", href: "/case-studies", desc: "Client wins and performance benchmarks" },
  { title: "Our Team", href: "/about-us", desc: "Our story, team, and approach" },
  { title: "Live Workshops", href: "/workshops", desc: "Live training events and conferences" },
  // The membership, right after the workshops it sells alongside. The line
  // reads the dates from shared/pro-pricing.ts so it stays true on its own.
  { title: "AdVenture Plus", href: "/membership", desc: PLUS_NAV_DESC, testId: "nav-membership" },
  {
    title: "Channels",
    links: [
      { label: "Google Ads", href: "/google-ads-management", desc: "Search, Shopping, PMax, Display, YouTube" },
      { label: "Meta Advertising", href: "/meta-ads-management", desc: "Facebook, Instagram, Advantage+" },
      { label: "LinkedIn Ads", href: "/linkedin-ads-agency", desc: "B2B pipeline and demand generation" },
      { label: "Pinterest Ads", href: "/pinterest-ads-agency", desc: "Visual discovery and shopping campaigns" },
      { label: "Programmatic", href: "/programmatic-advertising-agency", desc: "Display, video, CTV, and audio at scale" },
      { label: "Display Ads", href: "/display-ads", desc: "Awareness and retargeting across the web" },
      { label: "Retargeting", href: "/retargeting", desc: "Re-engage visitors across platforms" },
      { label: "Google Shopping", href: "/google-shopping-management", desc: "Product feed optimization and Shopping campaigns" },
      { label: "Google Local Service Ads", href: "/google-local-service-ads", desc: "Pay-per-lead for local service businesses" },
    ],
  },
  {
    title: "Industries",
    links: [
      { label: "Ecommerce", href: "/ecommerce", desc: "DTC, retail, and product-based businesses" },
      { label: "Lead Generation", href: "/lead-gen-services", desc: "B2B and B2C lead acquisition at scale" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Blog", href: "/blog", desc: "Insights on paid media, AI, and growth" },
      { label: "Claude Cowork Recipe Generator", href: "/claude-cowork-recipe-generator", desc: "Three personalized AI recipes for your business in 90 seconds" },
      { label: "AI Readiness Assessment", href: "/ai-readiness", desc: "Evaluate your team's AI maturity" },
      { label: "Consulting & Training", href: "/digital-advertising-consulting", desc: "Strategic advisory, audits, and in-house team training" },
      { label: "Careers", href: "/careers", desc: "Join our team" },
    ],
  },
];

/* ─── Shared styles ──────────────────────────────────────────────────────── */

const topLevelStyle: React.CSSProperties = {
  fontFamily: "var(--font-display)",
  fontSize: 20,
  fontWeight: 400,
  fontStyle: "italic",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--cream)",
};

const topLevelDescStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: 12,
  color: "hsla(48, 20%, 92%, 0.45)",
  marginTop: 2,
  display: "block",
  fontStyle: "normal",
  letterSpacing: "0.01em",
};

/* ─── Nav Item ───────────────────────────────────────────────────────────── */

function NavItem({ section, onClose }: { section: NavSection; onClose: () => void }) {
  const [open, setOpen] = useState(false);
  const isDirectLink = !!section.href && !section.links;

  if (isDirectLink) {
    return (
      <div style={{ borderBottom: "1px solid hsla(48, 20%, 92%, 0.10)" }}>
        <Link href={section.href!} onClick={onClose}>
          <div
            style={{ padding: "16px 28px", cursor: "pointer", transition: "background 150ms" }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "hsla(48, 20%, 92%, 0.03)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            data-testid={section.testId}
          >
            {section.testId === "nav-membership" ? (
              <>
                <span className="np-title np-plus-plain" style={topLevelStyle}>{section.title}</span>
                {/* The AdVenture+ lockup (wordmark + the chrome plus mark), shown
                    only on the 2026 pages by cowork-ds.css; hidden elsewhere. */}
                <span className="np-title np-plus-lockup" style={{ display: "none" }}>
                  <span className="sr-only">{section.title}</span>
                  <span aria-hidden="true">AdVenture</span>
                  <img
                    src="/images/icons/plus-mark-96.webp"
                    srcSet="/images/icons/plus-mark-96.webp 96w, /images/icons/plus-mark-192.webp 192w"
                    sizes="28px"
                    alt=""
                    aria-hidden="true"
                    className="np-plus-mark"
                    width={96}
                    height={96}
                  />
                </span>
              </>
            ) : (
              <span className="np-title" style={topLevelStyle}>{section.title}</span>
            )}
            {section.desc && <span className="np-desc" style={topLevelDescStyle}>{section.desc}</span>}
          </div>
        </Link>
      </div>
    );
  }

  return (
    <div style={{ borderBottom: "1px solid hsla(48, 20%, 92%, 0.10)" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 28px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          transition: "background 150ms",
          textAlign: "left",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "hsla(48, 20%, 92%, 0.03)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
      >
        <span className="np-title" style={topLevelStyle}>{section.title}</span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }} style={{ flexShrink: 0, marginLeft: 12 }}>
          <ChevronDown size={16} style={{ color: "var(--terra-lt)", opacity: 0.8 }} />
        </motion.div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.25, 0, 0.25, 1] }}
            style={{ overflow: "hidden" }}
          >
            <div style={{ padding: "0 28px 16px 28px" }}>
              {section.links?.map((link) => (
                <SubLinkItem key={link.href} link={link} onClose={onClose} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Sub Link Item ──────────────────────────────────────────────────────── */

function SubLinkItem({ link, onClose }: { link: NavLink; onClose: () => void }) {
  const handleHover = (e: React.MouseEvent, entering: boolean) => {
    const label = e.currentTarget.querySelector("[data-label]") as HTMLElement;
    if (label) label.style.color = entering ? "var(--cream)" : "hsla(48, 20%, 92%, 0.80)";
  };

  if (link.external) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: "block", padding: "8px 0", textDecoration: "none", cursor: "pointer", transition: "color 150ms" }}
        onMouseEnter={(e) => handleHover(e, true)}
        onMouseLeave={(e) => handleHover(e, false)}
      >
        <span data-label style={{ fontSize: 15, color: "hsla(48, 20%, 92%, 0.80)", transition: "color 150ms" }}>
          {link.label} <span style={{ fontSize: 10, opacity: 0.4 }}>↗</span>
        </span>
        {link.desc && (
          <span style={{ display: "block", fontSize: 14, color: "hsla(48, 20%, 92%, 0.45)", marginTop: 1 }}>
            {link.desc}
          </span>
        )}
      </a>
    );
  }

  return (
    <Link href={link.href} onClick={onClose}>
      <div
        style={{ display: "block", padding: "8px 0", cursor: "pointer", transition: "color 150ms" }}
        onMouseEnter={(e) => handleHover(e, true)}
        onMouseLeave={(e) => handleHover(e, false)}
      >
        <span data-label style={{ fontSize: 15, color: "hsla(48, 20%, 92%, 0.80)", transition: "color 150ms" }}>
          {link.label}
        </span>
        {link.desc && (
          <span style={{ display: "block", fontSize: 14, color: "hsla(48, 20%, 92%, 0.45)", marginTop: 1 }}>
            {link.desc}
          </span>
        )}
      </div>
    </Link>
  );
}

/* ─── Hidden scrollbar ───────────────────────────────────────────────────── */

const hideScrollbarCSS = `
  .nav-panel-root::-webkit-scrollbar { display: none; }
  .nav-panel-root { -ms-overflow-style: none; scrollbar-width: none; }
`;

/* ─── Main Panel ─────────────────────────────────────────────────────────── */

export function NavPanel({ isOpen, onClose }: NavPanelProps) {
  // The 2026 pages draw their buttons in on scroll/resize; nudge a redraw once
  // the panel has mounted so its CTAs get their drawn frames.
  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
      drawMenuButtons();
    }, 320);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  // Every workshop page gets the Vivid menu (menu-vivid.css), not just the
  // four that load cowork-ds.css.
  const [location] = useLocation();
  const vivid =
    location.startsWith("/workshops") ||
    location.startsWith("/events") ||
    (typeof document !== "undefined" && document.documentElement.classList.contains("ds-cowork-page"));

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Session-aware account entry — always present here (unlike the navbar's
  // top-row link, which drops on workshop detail pages) so mobile and
  // workshop-detail visitors always have a way in. While isPending, this
  // reads as logged-out; it self-corrects the instant the session settles.
  const { data: sessionData, isPending: sessionPending } = useSession();
  const isLoggedIn = !sessionPending && !!sessionData?.user;
  const navItems: NavSection[] = NAV_ITEMS;

  return (
    <>
      <style>{hideScrollbarCSS}</style>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="nav-panel"
            className={vivid ? "nav-panel-root np-vivid" : "nav-panel-root"}
            data-lenis-prevent
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 200,
              overflowY: "scroll",
              overflowX: "hidden",
              overscrollBehavior: "contain",
              background: "hsla(0, 0%, 0%, 0.4)",
            }}
            onClick={onClose}
          >
            <motion.div
              initial={{ x: 120, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 120, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.25, 0, 0.25, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="np-panel"
              style={{
                marginLeft: "auto",
                width: 420,
                maxWidth: "90vw",
                minHeight: "100%",
                background: "var(--charcoal)",
                borderLeft: "1px solid hsla(48, 20%, 92%, 0.10)",
              }}
            >
              {/* Close button */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  padding: "0 28px",
                  height: 72,
                  borderBottom: "1px solid hsla(48, 20%, 92%, 0.10)",
                }}
              >
                <button
                  onClick={onClose}
                  aria-label="Close navigation menu"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "hsla(48, 20%, 92%, 0.60)",
                    transition: "color 150ms",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = "var(--cream)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = "hsla(48, 20%, 92%, 0.60)"; }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 11,
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                    }}
                  >
                    Close
                  </span>
                  <X size={16} />
                </button>
              </div>

              {/* Nav items */}
              {navItems.map((section) => (
                <NavItem key={section.title} section={section} onClose={onClose} />
              ))}

              {/* Footer CTA */}
              <div style={{ padding: "20px 28px", borderTop: "1px solid hsla(48, 20%, 92%, 0.10)" }}>
                <Link href="/contact" onClick={onClose}>
                  <button
                    className="np-cta"
                    style={{
                      width: "100%",
                      height: 48,
                      background: "var(--indigo)",
                      color: "var(--cream)",
                      border: "none",
                      fontFamily: "var(--font-mono)",
                      fontSize: 12,
                      textTransform: "uppercase",
                      letterSpacing: "0.12em",
                      fontWeight: 500,
                      cursor: "pointer",
                      transition: "background 200ms",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "var(--indigo-lt)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "var(--indigo)"; }}
                  >
                    <span>Get a Proposal →</span>
                  </button>
                </Link>
                {/* Account entry — hamburger only per Isaac (2026-08-05):
                    a button below Get a Proposal, Log In vs Dashboard by
                    session state. */}
                {MEMBERS_PORTAL_LIVE && (
                  <Link href={isLoggedIn ? "/dashboard" : "/login"}>
                    <button
                      onClick={onClose}
                      data-testid={isLoggedIn ? "nav-dashboard" : "nav-signin"}
                      className="np-login"
                      style={{
                        display: "block",
                        width: "100%",
                        marginTop: 10,
                        padding: "14px 28px",
                        background: "transparent",
                        color: "var(--cream)",
                        border: "1.5px solid hsla(48, 20%, 92%, 0.35)",
                        fontFamily: "var(--font-mono)",
                        fontSize: 13,
                        textTransform: "uppercase" as const,
                        letterSpacing: "0.12em",
                        fontWeight: 500,
                        cursor: "pointer",
                        transition: "border-color 200ms, background 200ms",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "hsla(48, 20%, 92%, 0.08)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    >
                      <span>{isLoggedIn ? "Dashboard →" : "Log In →"}</span>
                    </button>
                  </Link>
                )}
                <div
                  style={{
                    marginTop: 12,
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    color: "hsla(48, 20%, 92%, 0.30)",
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    textAlign: "center",
                  }}
                >
                  New York · Philadelphia · Fort Lauderdale
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
